/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  CostCenterFinderDialogConfig,
  costCenterFinderDialogRoute,
  CostCenterFinderListItem,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { AelEvents, PolicyReportingMethod } from '@gh/builder-shared-data';
import { ActionFlow, EnumDictionary, getRouteInitData, getSingleNewSelectedEntity } from '@gh/common-shared';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$, map$ } from '@gh/rx/operators';
import { assign } from 'lodash';
import { SystemVarsManager, SystemVarsManagerStatistic } from '../../resources/model/system-vars.model';
import { SystemVarsManagerTabBusinessService } from '../../resources/system-vars-manager-tab-business.service';
import { SystemVarsManagerFormState } from './system-vars-manager-form.service';

@Component({
  selector: 'gh-system-vars-manager-tab',
  template: require('./system-vars-manager-tab.component.html'),
  providers: [SystemVarsManagerFormState],
})
export class SystemVarsManagerTabComponent extends AbstractFormComponent<SystemVarsManager> implements OnInit {
  policyReportingMethodDict: EnumDictionary<PolicyReportingMethod>;
  statistic: SystemVarsManagerStatistic;

  private original: SystemVarsManager;

  constructor(private route: ActivatedRoute,
              private userContext: BuilderUserContext,
              private dialogs: DialogService,
              private waitings: WaitingService,
              private notifications: NotificationService,
              private navigations: BuilderNavigationService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              public form: SystemVarsManagerFormState,
              private business: SystemVarsManagerTabBusinessService) {
    super();
  }

  get isFixed12MonthMethod(): boolean {
    return this.form.policyReportingMethod === PolicyReportingMethod.Fixed12Month;
  }

  get completeFlow(): ActionFlow<FormFlowState<SystemVarsManager>> {
    return this.pipelines.complete({
      execute: this.commonActions.execute(({ value }) => this.business.save(value)),
      finish: ({ value }) => {
        this.original = value;
        this.form.build(value);
      },
    });
  }

  ngOnInit(): void {
    const { details, ...init } = getRouteInitData(this.route);

    assign(this, init);

    this.original = details;
    this.form.build(details);
  }

  loadCompanies(event: AutocompleteLoadMoreEvent): void {
    event.load(this.business.findCompaniesByName(event.query));
  }

  onCommissionCcFind(): void {
    this.dialogs.openWithInput(costCenterFinderDialogRoute, <CostCenterFinderDialogConfig>{
        preselected: this.form.commissionCc && [this.form.commissionCc.id],
      })
      .afterClosed().pipe(
      filter$(Boolean),
      map$(getSingleNewSelectedEntity))
      .subscribe((cc: CostCenterFinderListItem) => this.form.setCommissionCc(cc));
  }

  onInterestCcFind(): void {
    this.dialogs.openWithInput(costCenterFinderDialogRoute, <CostCenterFinderDialogConfig>{
        preselected: this.form.interestCc && [this.form.interestCc.id],
      })
      .afterClosed().pipe(
      filter$(Boolean),
      map$(getSingleNewSelectedEntity))
      .subscribe((cc: CostCenterFinderListItem) => this.form.setInterestCc(cc));
  }

  onPolicyReportingMethodChange(): void {
    this.form.updateForm();
  }

  onShowAvailable(): void {
    this.business.getStatistic().pipe(
      (this.waitings.operator()))
      .subscribe(
        (statistic) => {
          this.statistic = {
            ...statistic,
            availableAnnual: this.form.policyAnnualAmount - statistic.jobsAnnual,
            availableUncomplete: this.form.policyUncompleteAmount - statistic.jobsUncomplete,
            availablePoliciesCount: this.form.policyNumberOfPolicies - statistic.jobsCount,
          };
        },
        this.notifications.processAll);
  }

  onUseFacilityChange(): void {
    this.form.updateForm();
  }

  onReset(): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.build(this.original));
  }

  goToAel(): void {
    this.navigations.goToAel({
      entityId: this.userContext.office && this.userContext.office.id,
      entityType: 'OFFICE',
      eventTypeIds: [AelEvents.Office.EditedManager],
      label: 'lbl_manager',
    });
  }
}
